import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../../services/api';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

// Get the backend URL from environment
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AdminProvider = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAdminAuth();
  }, []);

  const checkAdminAuth = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (token) {
        // Vérifier si le token est valide
        const response = await fetch(`${BACKEND_URL}/api/admin/verify`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAdminToken(token);
          setAdminUser(data.admin);
          setIsAdminAuthenticated(true);
        } else {
          localStorage.removeItem('adminToken');
        }
      }
    } catch (error) {
      console.error('Admin auth check failed:', error);
      localStorage.removeItem('adminToken');
    } finally {
      setLoading(false);
    }
  };

  const adminLogin = async (username, password) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        const data = await response.json();
        setAdminToken(data.access_token);
        setAdminUser({ username: data.username, role: data.role });
        setIsAdminAuthenticated(true);
        localStorage.setItem('adminToken', data.access_token);
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('Admin login error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const adminLogout = () => {
    setAdminToken(null);
    setAdminUser(null);
    setIsAdminAuthenticated(false);
    localStorage.removeItem('adminToken');
  };

  const adminApiCall = async (endpoint, options = {}) => {
    if (!adminToken) {
      throw new Error('No admin token available');
    }

    const defaultOptions = {
      headers: {
        'Authorization': `Bearer ${adminToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    const response = await fetch(
      `${BACKEND_URL}/api/admin${endpoint}`,
      { ...options, ...defaultOptions }
    );

    if (response.status === 401) {
      adminLogout();
      throw new Error('Admin session expired');
    }

    return response;
  };

  // API helpers pour l'administration
  const adminAPI = {
    // Dashboard
    getDashboard: async () => {
      const response = await adminApiCall('/dashboard');
      return response.json();
    },

    // Users
    getUsers: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await adminApiCall(`/users?${queryString}`);
      return response.json();
    },

    getUserById: async (userId) => {
      const response = await adminApiCall(`/users/${userId}`);
      return response.json();
    },

    getUserSecurity: async (userId) => {
      const response = await adminApiCall(`/users/${userId}/security`);
      return response.json();
    },

    resendVerificationEmail: async (userId) => {
      const response = await adminApiCall(`/users/${userId}/resend-verification`, {
        method: 'POST',
      });
      return response.json();
    },

    getUserActivity: async (userId) => {
      const response = await adminApiCall(`/users/${userId}/activity`);
      return response.json();
    },

    getUsersStats: async () => {
      const response = await adminApiCall('/stats/users');
      return response.json();
    },

    updateUser: async (userId, userData) => {
      const response = await adminApiCall(`/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });
      return response.json();
    },

    suspendUser: async (userId, suspendData) => {
      const response = await adminApiCall(`/users/${userId}/suspend`, {
        method: 'POST',
        body: JSON.stringify(suspendData),
      });
      return response.json();
    },

    banUser: async (userId, banData) => {
      const response = await adminApiCall(`/users/${userId}/ban`, {
        method: 'POST',
        body: JSON.stringify(banData),
      });
      return response.json();
    },

    unbanUser: async (userId) => {
      const response = await adminApiCall(`/users/${userId}/unban`, {
        method: 'POST',
      });
      return response.json();
    },

    resetUserPassword: async (userId, resetData) => {
      const response = await adminApiCall(`/users/${userId}/reset-password`, {
        method: 'POST',
        body: JSON.stringify(resetData),
      });
      return response.json();
    },

    sendPasswordResetEmail: async (userId) => {
      const response = await adminApiCall(`/users/${userId}/send-password-reset`, {
        method: 'POST',
      });
      return response.json();
    },

    deleteUser: async (userId) => {
      const response = await adminApiCall(`/users/${userId}`, {
        method: 'DELETE',
      });
      return response.json();
    },

    // Spots
    getSpots: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await adminApiCall(`/spots?${queryString}`);
      return response.json();
    },

    getSpotById: async (spotId) => {
      const response = await adminApiCall(`/spots/${spotId}`);
      return response.json();
    },

    updateSpot: async (spotId, spotData) => {
      const response = await adminApiCall(`/spots/${spotId}`, {
        method: 'PUT',
        body: JSON.stringify(spotData),
      });
      return response.json();
    },

    disableSpot: async (spotId, reason) => {
      const response = await adminApiCall(`/spots/${spotId}/disable`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      return response.json();
    },

    enableSpot: async (spotId) => {
      const response = await adminApiCall(`/spots/${spotId}/enable`, {
        method: 'POST',
      });
      return response.json();
    },

    deleteSpot: async (spotId) => {
      const response = await adminApiCall(`/spots/${spotId}`, {
        method: 'DELETE',
      });
      return response.json();
    },

    // Actions
    getAdminActions: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await adminApiCall(`/actions?${queryString}`);
      return response.json();
    },

    // ===== STATISTICS API =====
    getStatsOverview: async (days = 30) => {
      const response = await fetch(`${BACKEND_URL}/api/stats/overview?days=${days}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return response.json();
    },

    getStatsTimeline: async (days = 30, granularity = 'day') => {
      const response = await fetch(`${BACKEND_URL}/api/stats/timeline?days=${days}&granularity=${granularity}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return response.json();
    },

    getStatsSpots: async (limit = 20, sortBy = 'views') => {
      const response = await fetch(`${BACKEND_URL}/api/stats/spots?limit=${limit}&sort_by=${sortBy}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return response.json();
    },

    getStatsSpotDetail: async (spotId, days = 30) => {
      const response = await fetch(`${BACKEND_URL}/api/stats/spots/${spotId}?days=${days}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return response.json();
    },

    getStatsEngagement: async (days = 30) => {
      const response = await fetch(`${BACKEND_URL}/api/stats/engagement?days=${days}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return response.json();
    },

    getStatsRealtime: async () => {
      const response = await fetch(`${BACKEND_URL}/api/stats/realtime`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return response.json();
    },

    getStatsSearches: async (days = 30) => {
      const response = await fetch(`${BACKEND_URL}/api/stats/searches?days=${days}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return response.json();
    },

    getStatsTags: async () => {
      const response = await fetch(`${BACKEND_URL}/api/stats/tags`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return response.json();
    },

    getStatsGeographic: async () => {
      const response = await fetch(`${BACKEND_URL}/api/stats/geographic`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      return response.json();
    },

    // ===== PHOTOS API =====
    getPhotos: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await adminApiCall(`/photos?${queryString}`);
      return response.json();
    },

    updatePhoto: async (spotId, photoType, photoIndex, photoData) => {
      const response = await adminApiCall(`/photos/${spotId}/${photoType}/${photoIndex}`, {
        method: 'PUT',
        body: JSON.stringify(photoData),
      });
      return response.json();
    },

    deletePhoto: async (spotId, photoType, photoIndex) => {
      const response = await adminApiCall(`/photos/${spotId}/${photoType}/${photoIndex}`, {
        method: 'DELETE',
      });
      return response.json();
    },

    movePhoto: async (spotId, targetSpotId, photoType, photoIndex) => {
      const response = await adminApiCall(`/photos/${spotId}/move?target_spot_id=${targetSpotId}&photo_type=${photoType}&photo_index=${photoIndex}`, {
        method: 'POST',
      });
      return response.json();
    },

    // ===== COMMENTS API =====
    getComments: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await adminApiCall(`/comments?${queryString}`);
      return response.json();
    },

    updateComment: async (commentId, commentData) => {
      const content = commentData.content || commentData;
      const response = await adminApiCall(`/comments/${commentId}?content=${encodeURIComponent(content)}`, {
        method: 'PUT',
      });
      return response.json();
    },

    deleteComment: async (commentId) => {
      const response = await adminApiCall(`/comments/${commentId}`, {
        method: 'DELETE',
      });
      return response.json();
    },

    getCommentsStats: async () => {
      const response = await adminApiCall('/comments/stats');
      return response.json();
    },

    // ===== MODERATION API =====
    getModerationPending: async (params = {}) => {
      const queryString = new URLSearchParams(params).toString();
      const response = await adminApiCall(`/moderation/pending?${queryString}`);
      return response.json();
    },

    getModerationStats: async () => {
      const response = await adminApiCall('/moderation/stats');
      return response.json();
    },

    approveSpot: async (spotId, notes = null) => {
      const response = await adminApiCall(`/moderation/${spotId}/approve${notes ? `?notes=${encodeURIComponent(notes)}` : ''}`, {
        method: 'POST',
      });
      return response.json();
    },

    rejectSpot: async (spotId, reason) => {
      const response = await adminApiCall(`/moderation/${spotId}/reject?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
      });
      return response.json();
    },

    requestSpotChanges: async (spotId, changes) => {
      const response = await adminApiCall(`/moderation/${spotId}/request-changes?changes_requested=${encodeURIComponent(changes)}`, {
        method: 'POST',
      });
      return response.json();
    },

    // ===== USER ROLES API =====
    updateUserRole: async (userId, role, trustLevel = null) => {
      let url = `/users/${userId}/role?role=${role}`;
      if (trustLevel !== null) url += `&trust_level=${trustLevel}`;
      const response = await adminApiCall(url, { method: 'PUT' });
      return response.json();
    },

    getUsersByRole: async (role = null) => {
      const url = role ? `/users/by-role?role=${role}` : '/users/by-role';
      const response = await adminApiCall(url);
      return response.json();
    },
  };

  const value = {
    isAdminAuthenticated,
    adminUser,
    adminToken,
    loading,
    adminLogin,
    adminLogout,
    adminAPI,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};